/**
 * useJurisdictionSearch.ts
 *
 * Debounced typeahead hook against GET /api/admin/jurisdictions/search.
 *
 * Usage:
 *   const { results, loading } = useJurisdictionSearch(query, stateFilter);
 *
 * - Debounces 300 ms before firing
 * - Cancels in-flight requests via AbortController when query changes
 * - Returns empty results (not an error) if the query is blank or < 2 chars
 */

import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '@/lib/apiFetch';

export interface GeoPlaceResult {
  id: string;
  name: string;
  state: string;
  state_fips: string;
  jurisdiction_type: string;
  fips_code: string;
  lat: number;
  lon: number;
  county: string | null;
  county_fips: string | null;
  /** UUID of the matching jurisdictions row, or null if not yet in the system. */
  linked_jurisdiction_id: string | null;
  /** Full jurisdiction path (state.county.place) for the linked row. */
  jurisdiction_path?: string | null;
}

export interface ResolvedCounty {
  county: string | null;
  county_fips: string | null;
  geocode_failed: boolean;
  cached: boolean;
}

interface UseJurisdictionSearchResult {
  results: GeoPlaceResult[];
  loading: boolean;
  error: string | null;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 1;

export function useJurisdictionSearch(
  query: string,
  stateFilter?: string
): UseJurisdictionSearchResult {
  const [results, setResults] = useState<GeoPlaceResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Clear previous timer and abort previous request
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const params = new URLSearchParams({ q: trimmed });
        if (stateFilter) params.set('state', stateFilter);

        const res = await apiFetch(
          `/api/admin/jurisdictions/search?${params.toString()}`,
          { signal: controller.signal }
        );

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            (body as { error?: string }).error ?? `HTTP ${res.status}`
          );
        }

        const json = (await res.json()) as {
          success: boolean;
          data: GeoPlaceResult[];
        };
        setResults(json.data ?? []);
        setError(null);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return; // stale request
        setResults([]);
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [query, stateFilter]);

  return { results, loading, error };
}

/**
 * Resolve county for a selected geo_place.
 * Calls PATCH /api/admin/geo-places/:fips/resolve-county.
 * Returns null if the geocoder is unavailable (geocode_failed: true).
 * Never throws — callers do not need try/catch.
 */
export async function resolveGeoPlaceCounty(
  fips: string
): Promise<ResolvedCounty> {
  try {
    const res = await apiFetch(`/api/admin/geo-places/${fips}/resolve-county`, {
      method: 'PATCH',
    });
    if (!res.ok) {
      return {
        county: null,
        county_fips: null,
        geocode_failed: true,
        cached: false,
      };
    }
    const json = (await res.json()) as ResolvedCounty;
    return json;
  } catch {
    return {
      county: null,
      county_fips: null,
      geocode_failed: true,
      cached: false,
    };
  }
}
